import { Injectable } from '@angular/core';
import { DgControlType } from '../../components/dg-control-type';
import {
  FormViewModel, FormBindingType, FormViewModelField, FormVariable, FormVariableCategory,
  Subscription, FormDeclaration, FormComponent, FormSchemaEntity, FormSchemaEntityField
} from '../metadata';
import { mergeWith } from 'lodash-es';

export const ROOT_VIEW_MODEL_ID = 'root-viewmodel';

// 读取dom各节点
@Injectable({
  providedIn: 'root'
})
export class DomDocumentService {
  private document;

  constructor() { }

  loadDocument(jsonData: any) {
    if (!jsonData || !jsonData.module) {
      return;
    }
    this.document = jsonData;

    // 适配旧表单的VM 缺失type的场景  ---可以删除了，目前没有这么旧的表单了
    //  this._adaptOldFormVM();
    // this.updateOptions(this.document);

    this._adaptUpdateVersion();
  }

  getDocument() {
    return this.document;
  }
  get viewmodels() {
    return this.document.module.viewmodels;
  }
  set viewmodels(value) {
    this.document.module.viewmodels = value;
  }

  get components(): any[] {
    return this.document.module.components;
  }
  set components(value) {
    this.document.module.components = value;
  }

  get options() {
    return this.document.options;
  }

  set options(value) {
    this.document.options = value;
  }

  get module() {
    return this.document.module;
  }
  set module(value) {
    this.document.module = value;
  }
  /**
   * 获取schemas节点下的uri (目前仅支持单一数据源)
   */
  getSchemas() {
    const schemas = this.document.module.schemas;
    if (!schemas || schemas.length === 0) {
      return;
    }
    return schemas[0];

  }

  setSchemas(schemaObject) {
    if (!schemaObject) {
      return;
    }
    this.document.module.schemas = [schemaObject];
  }

  setSchemaEntity(schemEntities: FormSchemaEntity[]) {
    const schema = this.getSchemas();
    schema.entities = schemEntities;
  }

  getQDPInfo() {
    const qdpInfo = this.document.module.qdpInfo;
    if (qdpInfo && qdpInfo.qoMetadata && qdpInfo.qoMetadata.length) {
      return qdpInfo;
    } else {
      return null;
    }
  }

  getUpdateVersion() {
    return this.document.module.updateVersion;
  }

  get extraImports(): Array<{ name: string, path: string }> {
    return this.document.module.extraImports;
  }

  set extraImports(value: Array<{ name: string, path: string }>) {
    this.document.module.extraImports = value;
  }

  /**
   * 根据组件ID获取components下相应的组件节点
   * @param componentId 组件标识
   */
  getComponentById(componentId) {
    if (!this.components || this.components.length === 0) {
      return;
    }
    return this.components.find(cmp => cmp.id === componentId);
  }


  /**
   *  根据VM ID获取相应组件
   * @param viewModelId VMID
   */
  getComponentByVMId(viewModelId: string) {
    return this.components.find(cmp => cmp.viewModel === viewModelId);
  }

  /**
   * 根据viewModelId获取模型节点
   * @param viewModelId 视图模型标识
   */
  getViewModelById(viewModelId): FormViewModel {
    if (!this.viewmodels || this.viewmodels.length === 0 || !viewModelId) {
      return;
    }
    return this.viewmodels.find(vm => vm.id === viewModelId);
  }

  getViewModelFieldById(viewModelId: string, fieldId: string) {
    const vm = this.getViewModelById(viewModelId);
    return vm.fields.find(f => f.id === fieldId);
  }
  getViewModelByFieldId(fieldId: string): FormViewModel {
    let viewModel: FormViewModel;
    for (const vm of this.viewmodels) {
      const field = vm.fields.find(f => f.id === fieldId);
      if (field) {
        viewModel = vm;
        break;
      }
    }
    return viewModel;
  }
  /**
   * 校验指定VM下是否重复绑定字段或变量
   * @param viewModelId 视图模型标识
   * @param newFieldId 字段标识
   */
  checkViewModelDulplicated(viewModelId, newFieldId) {
    if (!viewModelId || !newFieldId) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);

    // 判断重复绑定
    const exsitVM = viewModel.fields.find(f => f.id === newFieldId);
    if (exsitVM) {
      return true;
    }
    return false;
  }
  /**
   * 修改ViewModel Field
   * @param viewModelId VM ID
   * @param fieldId 修改前binding.field取值
   * @param changeObject 变更集
   */
  modifyViewModelFieldById(viewModelId, fieldId, changeObject) {
    if (!viewModelId || !changeObject) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    let field;
    if (fieldId) {
      field = viewModel.fields.find(f => f.id === fieldId);
    }
    function customizer(objValue, srcValue) {
      if (Array.isArray(objValue)) {
        return srcValue;
      }
    }
    if (field) {
      // 数组类型不再合并，全量替换：用户枚举数据的更改
      mergeWith(field, changeObject, customizer);
    } else {
      changeObject.groupId = null;
      changeObject.groupName = null;
      this.addViewModelField(viewModelId, changeObject);
    }
  }

  /**
   * 控件新增绑定添加ViewModel Field
   */
  addViewModelField(viewModelId, filedObject: FormViewModelField) {
    if (!viewModelId || !filedObject) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    // if (filedObject.type === FormBindingType.Form) {
    viewModel.fields.push(filedObject);
    // }
  }

  /**
   * 根据VMID修改ViewModel节点
   * @param vmId  视图模型标识
   * @param vmFields 字段集合
   */
  setViewModelFieldsById(vmId, vmFields) {
    if (!this.viewmodels || this.viewmodels.length === 0 || !vmId) {
      return;
    }
    const oldVM = this.viewmodels.find(vm => vm.id === vmId);
    oldVM.fields = vmFields;
  }

  /**
   * 修改VM字段的分组名称
   * @param viewModelId 视图模型标识
   * @param groupId 分组标识
   * @param groupName 分组名称
   */
  modifyGroupNameById(viewModelId, groupId, groupName) {
    const vm = this.getViewModelById(viewModelId);
    vm.fields.forEach(field => {
      if (field.groupId !== groupId) { return; }
      field.groupName = groupName;
    });
  }

  /**
   * 根据ID删除ViewModel中的field节点
   * @param viewModelId 视图模型标识
   * @param fieldId 字段标识
   */
  deleteViewModelFieldById(viewModelId, fieldId) {
    if (!viewModelId || !fieldId) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    if (!viewModel) {
      return;
    }
    viewModel.fields = viewModel.fields.filter(f => f.id !== fieldId);
  }

  /**
   * 清除视图模型中针对字段的修改
   * @param viewModelId 视图模型标识
   * @param fieldId 字段标识
   */
  clearViewModelFieldSchema(viewModelId, fieldId) {
    if (!viewModelId) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    let field;
    if (fieldId) {
      field = viewModel.fields.find(f => f.id === fieldId);
    }
    if (field) {
      field.fieldSchema = {};
    }
  }
  /**
   * 根据ID删除整个ViewModel
   * @param viewModelId 视图模型标识
   */
  deleteViewModelById(viewModelId) {
    const index = this.viewmodels.findIndex(vm => vm.id === viewModelId);
    this.viewmodels.splice(index, 1);
  }

  /**
   * 删除dom中components下的组件节点
   * @param cmpId 组件ID
   */
  deleteComponent(cmpId: string) {
    if (!this.document || !this.document.module || !cmpId || !this.document.module.components) {
      return [];
    }
    this.document.module.components = this.document.module.components.filter(c => c.id !== cmpId);
  }

  /*
   * 获取表单引用的命令构件信息
   */
  getWebCmds(): any[] {
    return this.document.module.webcmds;
  }

  getServiceRefs(): any[] {
    return this.document.module.serviceRefs || [];
  }

  /**
   * 获取表单ViewModel中的命令，构建treetable数据，用于事件的选择窗口
   * 树表中额外增加commandPath属性(命令所在viewModelId.commandCode)，用于窗口展开时数据行的回显。
   */
  getCommandsTreeTable(viewModelId) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    const commandTree = [];
    // 获取根ViewModel和其他所有ViewModel的命令
    if (!viewModelId || viewModelId === ROOT_VIEW_MODEL_ID) {

      for (let index = 0; index < this.viewmodels.length; index++) {
        const viewModel = this.viewmodels[index];
        if (viewModel.fakeDel) {
          continue;
        }
        const children = [];

        viewModel.commands.forEach(command => {
          children.push({ data: { ...command, commandPath: viewModel.id + '.' + command.code }, children: [], selectable: true });
        });

        // if (index !== 0 && children.length === 0) {
        //     continue;
        // }
        commandTree.push({
          data: { id: viewModel.id, code: viewModel.code, name: viewModel.name + '视图模型', commandPath: viewModel.id },
          children,
          selectable: false, expanded: true
        });
      }
      let rootVm;
      if (commandTree.length > 1) {
        // 根节点
        rootVm = commandTree[0];
        const childVmList = commandTree.slice(1, commandTree.length);
        rootVm.children.push(...childVmList);
        return [rootVm];
      }
      return commandTree;
    }

    // 获取指定viewModel的命令
    const selectedViewModel = this.viewmodels.find(vm => vm.id === viewModelId);
    selectedViewModel.commands.forEach(command => {
      commandTree.push({
        data: { ...command, commandPath: selectedViewModel.id + '.' + command.code },
        children: [], selectable: true
      });
    });
    return [{
      data: {
        id: selectedViewModel.id, code: selectedViewModel.code, name: selectedViewModel.code + '视图模型',
        commandPath: selectedViewModel.id
      },
      children: commandTree,
      selectable: false,
      expanded: true
    }];

  }

  /**
   * 获取所有的命令，平铺成数组
   */
  getAllPlainCommands(): any[] {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    const commands = [];
    // 获取根ViewModel和其他所有ViewModel的命令
    this.viewmodels.forEach(viewModel => {
      viewModel.commands.forEach(command => {
        commands.push({ ...command, commandPath: viewModel.id + '.' + command.code });
      });
    });
    return commands;
  }

  /**
   * 获取ViewModel中组件上下文变量，构建treetable数据，用于变量绑定的选择窗口
   */
  getLocaleVariablesByViewModelId(viewModelId: string) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return [];
    }
    const viewModel = this.getViewModelById(viewModelId);
    if (!viewModel || !viewModel.states || viewModel.states.length === 0) {
      return [];
    }

    const children = [];
    viewModel.states.forEach(variable => {
      if (!variable.category || variable.category === FormVariableCategory.locale) {
        children.push({ data: { ...variable }, children: [], selectable: true });
      }
    });

    if (!children.length) {
      return [];
    }
    const rootVm = {
      data: { id: viewModel.id, code: viewModel.code, name: viewModel.name + '视图模型' },
      children,
      selectable: false,
      expanded: true,
      nodeType: 'vmNode'
    };

    return [rootVm];
  }
  /**
   * 获取ViewModel中远程（VO）上下文变量，构建treetable数据，用于变量绑定的选择窗口
   */
  getRemoteVariables() {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return [];
    }
    const viewModel = this.getViewModelById(ROOT_VIEW_MODEL_ID);
    if (!viewModel || !viewModel.states || viewModel.states.length === 0) {
      return [];
    }

    const children = [];
    viewModel.states.forEach(variable => {
      if (variable.category === FormVariableCategory.remote) {
        children.push({ data: { ...variable }, children: [], selectable: true });
      }
    });
    return children;
  }

  /**
   * 更新远程变量
   * @param varList 变量列表
   */
  updateRemoteVariables(varList) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    // varList.map(v => v.category = 'remote');
    const viewModel = this.getViewModelById(ROOT_VIEW_MODEL_ID);
    viewModel.states = viewModel.states.filter(s => !s.category || s.category === 'locale');

    const vmVarList = this.changeSchemaVariable2VMVariable(varList);
    viewModel.states.push(...vmVarList);
  }

  /**
   * schema中的变量转换为VM上的变量
   * @param schemaVarList 变量列表
   */
  private changeSchemaVariable2VMVariable(schemaVarList) {
    if (!schemaVarList || schemaVarList.length === 0) {
      return [];
    }
    const vmVarList: FormVariable[] = [];
    schemaVarList.forEach(v => {
      const rv: FormVariable = {
        id: v.id,
        code: v.code,
        name: v.name,
        defaultValue: v.defaultValue,
        type: v.type.name,
        category: 'remote'
      };
      if (v.$type !== 'SimpleField' && v.type.fields) {
        rv.type = 'Object';
        rv.fields = this.changeSchemaVariable2VMVariable(v.type.fields);
      }

      vmVarList.push(rv);
    });
    return vmVarList;
  }
  /**
   * 保存变量(全量)
   * @param states
   * @param viewModelId
   */
  saveVariables(states: FormVariable[], viewModelId: string) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    viewModel.states.length = 0;
    viewModel.states.push(...states);
  }

  /**
   * 获取指定变量
   */
  getVariableByIdAndVMID(varId: string, viewModelId: string) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    const viewModel = this.getViewModelById(viewModelId);
    const variable = viewModel.states.find(v => v.id === varId);
    return variable;
  }

  /**
   * 获取指定变量
   */
  getVariableById(varId: string) {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return;
    }
    for (const viewModel of this.viewmodels) {
      const variable = viewModel.states.find(v => v.id === varId);
      if (variable) {
        return variable;
      }
    }
  }
  /**
   * 获取所有VM下的变量，组装成树结构
   * 树表中额外增加statePath属性(命令所在viewModelId.variableCode)，用于窗口展开时数据行的回显。
   */
  getAllVariables() {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return [];
    }
    const vmTree = [];
    this.viewmodels.forEach(viewModel => {
      if (!viewModel || !viewModel.states || viewModel.states.length === 0) {
        return [];
      }

      const children = [];
      viewModel.states.forEach(variable => {
        children.push({ data: { ...variable, statePath: viewModel.id + '.' + variable.code }, children: [], selectable: true });
      });

      const rootVm = {
        data: { id: viewModel.id, code: viewModel.code, name: viewModel.name + '视图模型', statePath: viewModel.id },
        children,
        selectable: false, expanded: true
      };
      vmTree.push(rootVm);

    });
    return vmTree;
  }

  /**
   * 获取所有VM下的变量并平铺成数组
   * 额外增加statePath属性(命令所在viewModelId.variableCode)，用于唯一标识
   */
  getAllPlainVariables(): any[] {
    if (!this.viewmodels || this.viewmodels.length === 0) {
      return [];
    }
    const varArray = [];
    this.viewmodels.forEach(viewModel => {
      if (!viewModel || !viewModel.states || viewModel.states.length === 0) {
        return [];
      }
      viewModel.states.forEach(variable => {
        varArray.push({ ...variable, statePath: viewModel.id + '.' + variable.code });
      });
    });
    return varArray;
  }

  /**
   * 1月3号以前的盘中缺失updateVersion字段，默认添加181225（上一次执行表单升级工具后的统一版本号）
   */
  _adaptUpdateVersion() {
    if (!this.document.module.updateVersion) {
      this.document.module.updateVersion = '181225';
    }
  }


  /**
   * 根据字段ID获取VO字段信息（包括关联表字段） (此方法为适配旧表单viewModel.fieldName从VO取值的问题，待保留一段时间后删除)
   * @param id
   * @param fields
   */
  getFieldByOriginID(id: string, fields: FormSchemaEntityField[]) {
    let element: FormSchemaEntityField;
    for (const field of fields) {
      if (field.originalId === id) {
        element = field;
        break;
      } else {
        if (field.type && field.type.fields) {
          for (const assoField of field.type.fields) {
            if (assoField.originalId === id) {
              element = assoField;
              break;
            }
          }
          if (element) {
            break;
          }
        }
      }
    }

    return element;
  }


  /**
   * 获取表字段列表(此方法为适配旧表单viewModel.fieldName从VO取值的问题，待保留一段时间后删除)
   * @param entities
   * @param bindTo
   */
  private _getTableFieldsByBindTo(entities: FormSchemaEntity[], bindTo: string): FormSchemaEntityField[] {
    if (!entities || entities.length === 0) {
      return;
    }
    const splitIndex = bindTo.indexOf('/');
    if (splitIndex > -1) {
      bindTo = bindTo.slice(splitIndex + 1, bindTo.length);
    }

    for (const entity of entities) {
      const entityType = entity.type;
      if (!entityType) {
        return [];
      }
      if (bindTo === '' || bindTo === entity.code || bindTo === entity.label) {
        return entityType.fields;
      }
      if (entityType.entities && entityType.entities.length > 0) {
        const fields = this._getTableFieldsByBindTo(entityType.entities, bindTo);
        if (fields) {
          return fields;
        }
      }
    }

  }

  /**
   * 获取表Label(此方法为适配旧表单viewModel.fieldName从VO取值的问题，待保留一段时间后删除)
   * @param entities
   * @param bindTo
   */
  private getTableLabelByBindTo(entities: FormSchemaEntity[], bindTo: string): string {
    if (!entities || entities.length === 0) {
      return;
    }
    const splitIndex = bindTo.indexOf('/');
    if (splitIndex > -1) {
      bindTo = bindTo.slice(splitIndex + 1, bindTo.length);
    }

    for (const entity of entities) {
      const entityType = entity.type;
      if (!entityType) {
        return '';
      }
      if (bindTo === '' || bindTo === entity.code || bindTo === entity.label) {
        return entity.label;
      }
      if (entityType.entities && entityType.entities.length > 0) {
        const label = this.getTableLabelByBindTo(entityType.entities, bindTo);
        if (label) {
          return label;
        }
      }
    }
  }


  /**
   * 获取组件声明
   */
  getComponentDeclarations(): FormDeclaration {
    if (!this.document || !this.document.module) {
      return {};
    }
    return this.document.module.declarations;
  }
  /**
   * 修改组件事件声明
   * @param data
   */
  setComponentDeclarations(data) {
    this.document.module.declarations = data;
  }

  /**
   * 获取事件订阅
   */
  getComponentSubscriptions(sourceComponent = ''): Subscription[] {
    if (!this.document || !this.document.module || !this.document.module.subscriptions ||
      this.document.module.subscriptions.length === 0) {
      return [];
    }
    if (!sourceComponent) {
      return this.document.module.subscriptions;
    }
    const subList = this.document.module.subscriptions.filter(sub => sub.sourceComponent === sourceComponent);
    return subList;
  }

  /**
   * 修改事件订阅
   * @param data
   */
  setComponentSubscriptions(data = []) {
    if (!this.document || !this.document.module) {
      return;
    }
    this.document.module.subscriptions = data;
  }

  deleteComponentSubscription(cmpId: string) {
    if (!this.document || !this.document.module || !this.document.module.subscriptions) {
      return;
    }

    const subscriptions = this.document.module.subscriptions;
    this.document.module.subscriptions = subscriptions.filter(
      (item) => item.sourceComponent !== cmpId
    );
  }

  /**
   * 设置外部组件信息
   * @param cmpInfo 组件信息
   * @param cmpId 外部组件id
   */
  setExternalComponent(cmpInfo: any, cmpId: string) {
    if (!this.document || !this.document.module) {
      return;
    }
    if (!this.document.module.externalComponents) {
      this.document.module.externalComponents = [];
    }
    if (!cmpId) { // 新增
      if (cmpInfo.type === DgControlType.ModalContainer) { // ModalContainer与内部实体是一对一映射
        const externalComponentArray = this.getExternalComponent(cmpInfo.containerId);
        if (externalComponentArray !== undefined && externalComponentArray !== null && externalComponentArray.length > 0) {
          externalComponentArray.forEach(externalComponent => {
            this.deleteExternalComponent(externalComponent.id);
          });
        }
      }
      this.document.module.externalComponents.push(cmpInfo);
    } else {
      // 修改
      this.document.module.externalComponents = this.document.module.externalComponents.filter(e => e.id !== cmpId);
      this.document.module.externalComponents.push(cmpInfo);

    }

    // 更新组合表单标识
    if (this.document.module.externalComponents.length === 0) {
      this.document.module.isComposedFrm = false;
    } else {
      this.document.module.isComposedFrm = true;
    }
  }

  /**
   * 获取外部组件
   * @param cmpId
   */
  getExternalComponent(cmpId = '') {
    if (!this.document || !this.document.module || !this.document.module.externalComponents) {
      return [];
    }

    // 筛选当前生效的外部组件：一些场景下externalComponents节点下会出现冗余的组件 TODO 待优化
    const compStr = JSON.stringify(this.document.module.components);
    if (this.document.module.externalComponents && this.document.module.externalComponents.length) {
      this.document.module.externalComponents = this.document.module.externalComponents.filter(element => {
        if (compStr.indexOf(element.id) < 0) {
          return false;
        }
        return true;
      });
    }

    if (!cmpId) {
      return this.document.module.externalComponents;
    }
    return this.document.module.externalComponents.find(e => e.id === cmpId);
  }

  /**
   * 删除外部组件
   * @param cmpId
   */
  deleteExternalComponent(cmpId: string) {
    if (!this.document || !this.document.module || !cmpId || !this.document.module.externalComponents) {
      return [];
    }
    this.document.module.externalComponents = this.document.module.externalComponents.filter(c => c.id !== cmpId);

    // 更新组合表单标识
    if (this.document.module.externalComponents.length === 0) {
      this.document.module.isComposedFrm = false;
    } else {
      this.document.module.isComposedFrm = true;
    }
  }

  /**
   * 获取指定VM关联的组件中绑定指定字段的控件（目前只有一个控件）
   * @param viewModelId VM标识
   * @param fieldId   字段标识
   */
  getControlsInCmpWidthBinding(viewModelId, fieldId) {
    const cmp = this.components.find(c => c.viewModel === viewModelId);

    return this.getControlsByBinding(cmp.contents, fieldId);
  }

  /**
   * 获取指定VM关联的组件中绑定指定字段的控件
   * @param contents DOM节点
   * @param fieldId 字段标识
   */
  private getControlsByBinding(contents, fieldId) {
    const controls = [];
    for (const element of contents) {
      if (element.type === DgControlType.DataGrid || element.type === DgControlType.TreeGrid) { // 列表
        const childControls = this.getControlsByBinding(element.fields, fieldId);
        controls.push(...childControls);
      } else if (element.contents) { // 容器组件
        const childControls = this.getControlsByBinding(element.contents, fieldId);
        controls.push(...childControls);
      } else if (element.binding && element.binding.type === FormBindingType.Form && element.binding.field === fieldId) {
        controls.push(element);
      }
    }

    return controls;
  }

  /** 获取参数映射对应的参数
   * queryId 查询ID
   */
  getParasMappingEditorParams(queryId: any, qoManagerCode: any, queryRelativeUrl: any) {
    const editorParams: any = {};
    editorParams.queryId = queryId;
    editorParams.datasourceid = qoManagerCode;
    editorParams.queryRelativeUrl = queryRelativeUrl;
    return editorParams;
  }

  private updateOptions(domJson) {
    domJson.options = domJson.options || {};
    if (domJson.module) {
      const templateType = domJson.module.bootstrap;
      if (templateType === 'card-template') {
        domJson.options['enableDragAndDropToModifyLayout'] = true;
      } else {
        domJson.options['enableDragAndDropToModifyLayout'] = false;
      }
    }
  }

  /**
   * 根据id路径定位节点
   * @param rootNode 根节点
   * @param idPath id路径
   */
  getNodeByIdPath(rootNode: any, idPath: string) {
    if (!rootNode || !idPath) {
      return null;
    }
    const idPathArray = idPath.split('.');
    if (idPathArray.length === 0) {
      return;
    }
    const currentId = idPathArray.shift();
    if (rootNode.contents) {
      for (const item of rootNode.contents) {

        if ((currentId.includes('*') && item.id.startsWith(currentId.slice(0, currentId.length - 1))) || item.id === currentId) {

          if (idPathArray.length === 0) {
            return item;
          } else {
            const found = this.getNodeByIdPath(item, idPathArray.join('.'));
            if (found) {
              return found;
            }
          }
        }
      }
    }
    return null;
  }

  /**
   *  获取节点下所有 components ID 列表
   * @param eleContents [] 
   * @returns string[]
   */
  getALLComponentsIDList(eleContents: { type: string, component: any, contents: [] }[]): string[] {
    let deIDList = [];
    eleContents.forEach(element => {
      if (element.type == 'ComponentRef') {
        let targetComponents = this.components.find((item) => {
          return item.id == element.component;
        });
        deIDList.push(targetComponents.id);
        if (targetComponents && targetComponents.contents) {
          if (targetComponents.contents && targetComponents.contents.length) {
            deIDList = deIDList.concat(this.getALLComponentsIDList(targetComponents.contents));
          }
        }
      }
      if (element.contents && element.contents.length) {
        deIDList = deIDList.concat(this.getALLComponentsIDList(element.contents));
      }
    });
    return deIDList;
  }

  /**
   * 添加component 方法，暂时只根据 bindTo 校验唯一性
   *
   * @param component
   * @memberof DomService
   */
  addComponent(component: FormComponent): string {
    let oldComponentIndex = 0;
    let oldComponent = this.components.find((item, index) => {
      oldComponentIndex = index;
      return item.viewModel == component.viewModel;
    });
    if (oldComponent) {
      component.id = oldComponent.id;
      this.components[oldComponentIndex] = component;
    } else {
      this.components.push(component);
    }
    return component.id;
  }

  /**
   * 添加 viewModel 方法，假设已经存在一致指向（bindTo）同一数据源的话则执行更新viewModel操作
   * 
   * @param viewModel：FormViewModel
   * @memberof DomService
   * @returns string viewModel id
   */
  public addViewModel(viewModel: FormViewModel): string {
    let oldViewModelIndex = 0;
    let oldViewModel = this.viewmodels.find((item, index) => {
      oldViewModelIndex = index;
      return item.bindTo == viewModel.bindTo;
    });
    if (oldViewModel) {
      viewModel.id = oldViewModel.id;
      this.viewmodels[oldViewModelIndex] = viewModel;
    } else {
      this.viewmodels.push(viewModel);
    }
    return viewModel.id;
  }


  remarkFakeDel(removedList: string[]): void {
    removedList.forEach(element => {
      const targetComponent = this.getComponentById(element);
      const targetViewModel = this.getViewModelById(targetComponent.viewModel);
      targetViewModel['fakeDel'] = true;
    });
  }
}


export class ExternalComponentInfo {
  public id: string;
  public uri: string;
  public name: string;
  public code: string;
  public fileName: string;
  public filePath: string;
  public declarations: [];
  public type: string;
  public containerId: string;
  public url: string;
}
